import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  makeStyles,
} from '@material-ui/core';
import {
  MarkdownContent,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  configApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import useAsync from 'react-use/lib/useAsync';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import { chartSummaryApiRef } from '../../api';
import { sortSemver } from '../../utils/sorting';
import { RequirePermission } from '@backstage/plugin-permission-react';
import {
  ChartSummaryResponse,
  chartSummaryReadPermission,
  getEnvironmentCommonName,
} from '@internal/plugin-dev-services-chart-summary-common';
import { PermissionDeniedCard } from '../ErrorCards';
import { NotFoundCard } from '../ErrorCards/ErrorCards';

const useStyles = makeStyles(theme => ({
  gridItemCard: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100% - 10px)', // for pages without content header
    marginBottom: '10px',
  },
  cardHeader: {
    padding: '16px 16px 16px 20px',
  },
  outdatedChart: {
    color: theme.palette.status.warning,
  },
  currentChart: {
    color: theme.palette.status.ok,
  },
  gridItemCardContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
  },
  description: {
    wordBreak: 'break-word',
    paddingBlock: '4px',
    marginTop: '0px !important',
    marginBottom: '16px',
  },
  chartVersion: {
    margin: 0,
    marginTop: '4px',
  },
  serviceEnvironment: {
    fontStyle: 'italic',
    margin: 0,
    marginLeft: '6px',
  },
  contentPadding: {
    paddingBlock: '16px',
    width: '-webkit-fill-available',
    display: 'flex',
    flexDirection: 'column',
  },
  fieldHeading: {
    textTransform: 'uppercase',
    overflow: 'hidden',
    fontSize: '10px',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
    letterSpacing: '0.5px',
    color: theme.palette.text.secondary,
    marginTop: '0px',
    marginBottom: '2px',
  },
}));

export enum CHART_TYPES {
  PIPELINE,
  SERVICE,
}

const chartOutOfDate = (chart: string, currentChart: string) => {
  return sortSemver(chart, currentChart) === -1;
};

type SummaryCardContentProps = {
  response: ChartSummaryResponse;
  classes: ClassNameMap;
  frontendUrl: string;
};

const PipelineChartDisplay = ({
  response,
  classes,
  frontendUrl,
}: Omit<SummaryCardContentProps, 'entityType'>) => {
  const { latestVersion, environments } = response['brightly-pipeline'];
  const latestPipelineVersion = latestVersion;
  const pipelineVersion = environments[0]?.chart.version;
  const pipelineOutOfDate = chartOutOfDate(
    pipelineVersion,
    latestPipelineVersion,
  );
  return (
    <div className={classes.contentPadding}>
      <div style={{ flex: 1 }}>
        <h2 className={classes.fieldHeading}>Pipeline Chart</h2>
        <p
          className={`${classes.description} ${
            pipelineOutOfDate ? classes.outdatedChart : classes.currentChart
          }`}
        >
          {pipelineVersion}
        </p>
      </div>
      {pipelineOutOfDate && (
        <div>
          <div className={classes.fieldHeading}>
            <span className={classes.outdatedChart}>WARNING: </span>Pipeline
            Chart Out of Date
          </div>
          <MarkdownContent
            className={classes.description}
            content={`The latest Brightly-Pipeline version is [${latestPipelineVersion}](${frontendUrl}/docs/default/component/brightly-pipeline/${latestPipelineVersion?.charAt(
              0,
            )}/Brightly-Pipeline-${latestPipelineVersion})`}
          />
        </div>
      )}
    </div>
  );
};

const ServiceChartDisplay = ({
  response,
  classes,
  frontendUrl,
}: SummaryCardContentProps) => {
  const serviceChartDisplay: React.JSX.Element[] = [];
  const { latestVersion, environments } = response['brightly-service'];

  let servicesOutdated = false;
  let serviceVersionMap = new Map<string, React.JSX.Element[]>();
  for (const env of environments) {
    const envs = serviceVersionMap.get(env.chart.version);
    const envName = getEnvironmentCommonName(env);
    const envElement = (
      <p key={envName} className={classes.serviceEnvironment}>
        {envName}
      </p>
    );
    envs
      ? envs.push(envElement)
      : serviceVersionMap.set(env.chart.version, [envElement]);
  }
  for (const [version, envs] of serviceVersionMap.entries()) {
    const outOfDate = chartOutOfDate(version, latestVersion);
    servicesOutdated = servicesOutdated || outOfDate;
    serviceChartDisplay.push(
      <div key={version}>
        <p
          className={`${classes.chartVersion} ${
            outOfDate ? classes.outdatedChart : classes.currentChart
          }`}
        >
          {version}
        </p>
        {envs}
      </div>,
    );
  }
  return (
    <div className={classes.contentPadding}>
      <div style={{ flex: 1 }}>
        <h2 className={classes.fieldHeading}>{`Service Charts`}</h2>
        {serviceChartDisplay}
      </div>
      {servicesOutdated && (
        <div>
          <div className={classes.fieldHeading}>
            <span className={classes.outdatedChart}>WARNING: </span>Service
            Chart Out of Date
          </div>
          <MarkdownContent
            className={classes.description}
            content={`The latest Brightly-Service version is [${latestVersion}](${frontendUrl}/docs/default/component/brightly-service/${latestVersion?.charAt(
              0,
            )}/Brightly-service-${latestVersion})`}
          />
        </div>
      )}
    </div>
  );
};

const SummaryCardContent = (props: SummaryCardContentProps) => {
  const serviceEnvironments =
    props.response['brightly-service']?.environments ?? [];
  return (
    <>
      <PipelineChartDisplay {...props} />
      {serviceEnvironments.length !== 0 && <ServiceChartDisplay {...props} />}
    </>
  );
};

export function ChartSummaryCard() {
  const classes = useStyles();
  const { entity } = useEntity();
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const chartSummaryApi = useApi(chartSummaryApiRef);
  const frontendUrl = configApi.getString('app.baseUrl');
  const { value, loading, error } = useAsync(async () => {
    const { token } = await identityApi.getCredentials();

    if (!token) {
      throw new Error('Could not retrieve token');
    }

    const query =
      entity.spec?.type === 'repository'
        ? { parentApplication: entity.metadata.name }
        : { applicationId: entity.metadata.uid! };

    return chartSummaryApi.getChartSummary(query);
  }, [identityApi, chartSummaryApi]);

  return (
    <Card className={classes.gridItemCard}>
      <CardHeader
        className={classes.cardHeader}
        title="Pipeline and Service Charts"
      />
      <Divider />
      <RequirePermission
        permission={chartSummaryReadPermission}
        errorPage={
          <PermissionDeniedCard className={classes.gridItemCardContent} />
        }
      >
        <CardContent className={classes.gridItemCardContent}>
          {loading && <Progress />}
          {error &&
            ((error as any).status === 404 ? (
              <NotFoundCard className={classes.gridItemCardContent} />
            ) : (
              <ResponseErrorPanel error={error} />
            ))}
          {value && (
            <SummaryCardContent
              frontendUrl={frontendUrl}
              classes={classes}
              response={value}
            />
          )}
        </CardContent>
      </RequirePermission>
    </Card>
  );
}
